import type { CSSProperties } from 'react'

type Quad<T> = readonly [T, T, T, T]

type SparklesProps = {
  /**
   * Override the four sparkle colours. Defaults to the punk brand cycle:
   * purple, light purple, chartreuse-light, brand green. The four rendered
   * sparkles always take one colour each, in a seed-shuffled order, so every
   * cluster shows the full brand palette.
   */
  colours?: Quad<string>
  /**
   * Per-instance seed. Different seeds produce different position picks,
   * colour ordering, shape mix, and timing offset, so multiple Sparkles on
   * one page desync from each other and don't pop in unison. Use any small
   * integer that's stable across renders for this spot.
   */
  seed?: number
  /** Bump sparkles in front of overlay layers when needed. */
  z?: number
}

const DEFAULT_COLOURS: Quad<string> = ['#8a2585', '#e995e8', '#8bfa6e', '#00a134']

/**
 * Eight position/timing variants defined in `globals.css`. Picking four
 * starting at a seeded offset (and stepping by 3 to spread them around the
 * box) gives every sticker a different placement pattern.
 */
const POSITIONS = [
  'sparkle-1',
  'sparkle-2',
  'sparkle-3',
  'sparkle-4',
  'sparkle-5',
  'sparkle-6',
  'sparkle-7',
  'sparkle-8',
] as const

/**
 * Stable per-seed permutations of [0,1,2,3] so every instance shows all four
 * brand colours, just in a different order. Never repeats a colour within
 * one cluster.
 */
const COLOUR_ORDERS: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 1, 2, 3],
  [1, 3, 0, 2],
  [2, 0, 3, 1],
  [3, 2, 1, 0],
  [0, 2, 3, 1],
  [1, 0, 2, 3],
  [2, 3, 1, 0],
  [3, 1, 0, 2],
]

/**
 * Four brand-coloured cross sparkles that pop on long, off-beat loops. Pair
 * with any container that has `position: relative` (price stickers, CTA
 * buttons, badges). Driven by `.sparkle` / `.sparkle-N` CSS in `globals.css`.
 *
 * Per-instance variation: position picks, colour ordering, an asterisk-shape
 * accent, and a `--sparkle-offset` time shift all rotate with `seed`. Aria-
 * hidden, purely decorative.
 */
export function Sparkles({ colours = DEFAULT_COLOURS, seed = 0, z }: SparklesProps) {
  const positionStart = seed % POSITIONS.length
  const order = COLOUR_ORDERS[seed % COLOUR_ORDERS.length]!
  // Coprime step of 1.7s avoids clustering between seeds. Clamp to 0-8s.
  const offsetSec = ((seed * 1.7) % 8).toFixed(2)
  // Rotate which sparkle wears the asterisk shape so it's never the same
  // position twice in a row.
  const asteriskIndex = seed % 4

  return (
    <>
      {order.map((colourIdx, i) => {
        const posClass = POSITIONS[(positionStart + i * 3) % POSITIONS.length]
        const shapeClass = i === asteriskIndex ? ' sparkle-asterisk' : ''
        const style: CSSProperties = {
          color: colours[colourIdx],
          ['--sparkle-offset' as string]: `${offsetSec}s`,
          ...(z !== undefined ? { zIndex: z } : {}),
        }
        return (
          <span
            key={`${posClass}-${i}`}
            aria-hidden="true"
            className={`sparkle ${posClass}${shapeClass}`}
            style={style}
          />
        )
      })}
    </>
  )
}
